import React, { useState } from 'react';
import DynamicStepCard from 'design-system/src/components/DynamicStepCard/DynamicStepCard';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { useNavigate } from 'react-router-dom';

import gradientPrescriptionIcon from 'design-system/src/assets/icons/gradientRx.png';
import gradientProviderIcon from 'design-system/src/assets/icons/gradientProvider.png';
import gradientOfficeIcon from 'design-system/src/assets/icons/gradientOffice.png';
import gradientPeopleIcon from 'design-system/src/assets/icons/gradientPeople.png';
import { useQuery } from '@tanstack/react-query';
import { client } from '../../api';
import type { OrgData } from '../../api';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import AddBatchProvidersModal from './onboardingStepModals/AddBatchProvidersModal';
import AddOfficesModal from './onboardingStepModals/AddOfficesModal';
import AddMembersModal from '../settings/AddMembersModal/AddMembersModal';
import gradientEhrIcon from 'design-system/src/assets/icons/gradientEhr.png';
import { SUPPORTED_EHR_NAMES, SupportedEhrName } from '../../types';
import { PracticeOnboardingStatuses } from '../../api';
import EhrIntegrationModal from './onboardingStepModals/EhrIntegrationModal';
import * as Sentry from '@sentry/react';
import gradientPhoneIcon from 'design-system/src/assets/icons/gradientPhone.png';
import gradientBellIcon from 'design-system/src/assets/icons/gradientBell.png';
import AddBatchPhoneNumbersModal from './onboardingStepModals/AddBatchPhoneNumbersModal';
import SendFirstPrescriptionModal from './onboardingStepModals/SendFirstPrescriptionModal';
import { getEhrDisplayName } from '../../utils';
import { NOTIFICATION_PREFERENCES_SETTINGS_PATH, ONBOARDING_STEP_COPY } from './onboardingStepDefinitions';
import { createTrackingProps } from 'shared-utils/analytics';

const STEP_CARD_WRAPPER_CLASS = 'flex flex-col [&>div]:flex-1';
const STEP_CARD_ICON_CLASS = 'size-[46px] object-contain';

const AdminOnboardingSteps: React.FC = () => {
  const [modalOpen, setModalOpen] = useState<'addProviders' | 'addOffices' | 'addUsers' | null>(null);

  const { data: orgMembersData } = useQuery<OrgData>({
    queryKey: ['orgMembers'],
    queryFn: () => client.get('org-members').then((res) => res.data),
  });

  return (
    <>
      <div className={STEP_CARD_WRAPPER_CLASS}>
        <DynamicStepCard
          icon={<img src={gradientPeopleIcon} aria-hidden={true} className={STEP_CARD_ICON_CLASS} />}
          title="Invite your teammates"
          subtitle="Bring your team into Tandem to manage prescriptions, enrollments, and PAs together."
          primaryButton={{
            label: 'Add',
            onClick: () => setModalOpen('addUsers'),
            ...createTrackingProps({ elementName: 'Onboarding Steps: Invite Teammates' }),
          }}
        />
      </div>
      <div className={STEP_CARD_WRAPPER_CLASS}>
        <DynamicStepCard
          icon={<img src={gradientProviderIcon} aria-hidden={true} className={STEP_CARD_ICON_CLASS} />}
          title="Add more providers"
          subtitle="Enter each provider's National Provider Identifier (NPI) so we can match prescriptions correctly."
          primaryButton={{
            label: 'Add',
            onClick: () => setModalOpen('addProviders'),
            ...createTrackingProps({ elementName: 'Onboarding Steps: Add Providers' }),
          }}
        />
      </div>
      <div className={STEP_CARD_WRAPPER_CLASS}>
        <DynamicStepCard
          icon={<img src={gradientOfficeIcon} aria-hidden={true} className={STEP_CARD_ICON_CLASS} />}
          title="Add your office address"
          subtitle="Ensures materials arrive and prescriptions stay properly matched to your office."
          primaryButton={{
            label: 'Add',
            onClick: () => setModalOpen('addOffices'),
            ...createTrackingProps({ elementName: 'Onboarding Steps: Add Office Address' }),
          }}
        />
      </div>
      <AddBatchProvidersModal open={modalOpen === 'addProviders'} onClose={() => setModalOpen(null)} />
      <AddOfficesModal open={modalOpen === 'addOffices'} onClose={() => setModalOpen(null)} />
      {modalOpen === 'addUsers' && (
        <AddMembersModal onClose={() => setModalOpen(null)} members={orgMembersData?.members ?? []} showAddedUsers />
      )}
    </>
  );
};

interface OnboardingStepsProps {
  isAdmin: boolean;
  practiceOnboardingStatuses: PracticeOnboardingStatuses;
}

export const OnboardingSteps: React.FC<OnboardingStepsProps> = ({ isAdmin, practiceOnboardingStatuses }) => {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState<'ehrIntegration' | 'addPhoneNumbers' | 'sendFirstPrescription' | null>(
    null,
  );

  const { data: phoneNumbersData, isLoading: isLoadingPhoneNumbers } = useQuery({
    queryKey: ['office-phone-numbers'],
    queryFn: () => client.get('/settings/phone-numbers'),
  });

  const {
    data: ehrData,
    isLoading: isLoadingEhrName,
    isError: isErrorEhrName,
  } = useQuery({
    queryKey: ['ehrName'],
    queryFn: () => client.get(`/onboarding/ehr-name`).then((res) => res.data),
  });
  const supportedEhr = ehrData?.ehr && SUPPORTED_EHR_NAMES.includes(ehrData.ehr) ? ehrData.ehr : null;

  if (isLoadingEhrName) {
    return <LoadingSpinner height="600px" />;
  }

  // Report but don't block onboarding page on this error
  if (isErrorEhrName) {
    Sentry.captureException(new Error('Error loading EHR name'));
  }

  return (
    <div className="flex flex-col gap-4">
      <Typography styledAs="h6" renderedAs="h2">
        Get started with Tandem
      </Typography>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {supportedEhr && practiceOnboardingStatuses.ehr_integration_status === 'awaiting_credentials' && (
          <div className={STEP_CARD_WRAPPER_CLASS}>
            <DynamicStepCard
              icon={<img src={gradientEhrIcon} aria-hidden={true} className={STEP_CARD_ICON_CLASS} />}
              title={`Connect to ${getEhrDisplayName(supportedEhr)}`}
              subtitle="Tandem can automatically extract patient and insurance information so you can complete prior authorizations faster."
              primaryButton={{
                label: 'Connect',
                onClick: () => setModalOpen('ehrIntegration'),
                ...createTrackingProps({ elementName: 'Onboarding Steps: Connect EHR' }),
              }}
            />
          </div>
        )}
        {isAdmin && <AdminOnboardingSteps />}
        <div className={STEP_CARD_WRAPPER_CLASS}>
          <DynamicStepCard
            icon={<img src={gradientPhoneIcon} aria-hidden={true} className={STEP_CARD_ICON_CLASS} />}
            title="Add office numbers"
            subtitle="Add any phone numbers that may appear on prescriptions for accurate routing."
            primaryButton={{
              label: 'Add',
              onClick: () => setModalOpen('addPhoneNumbers'),
              ...createTrackingProps({ elementName: 'Onboarding Steps: Add Office Numbers' }),
            }}
          />
        </div>
        <div className={STEP_CARD_WRAPPER_CLASS}>
          <DynamicStepCard
            icon={<img src={gradientPrescriptionIcon} aria-hidden={true} className={STEP_CARD_ICON_CLASS} />}
            title="Queue up prescriptions"
            subtitle="You can begin sending prescriptions now—we'll hold them until your account is verified."
            primaryButton={{
              label: 'Get started',
              onClick: () => setModalOpen('sendFirstPrescription'),
              ...createTrackingProps({ elementName: 'Onboarding Steps: Queue Prescriptions' }),
            }}
          />
        </div>
        <div className={STEP_CARD_WRAPPER_CLASS}>
          <DynamicStepCard
            icon={<img src={gradientBellIcon} aria-hidden={true} className={STEP_CARD_ICON_CLASS} />}
            title={ONBOARDING_STEP_COPY.notificationPreferences.title}
            subtitle={ONBOARDING_STEP_COPY.notificationPreferences.description}
            primaryButton={{
              label: 'Set up',
              onClick: () => navigate(NOTIFICATION_PREFERENCES_SETTINGS_PATH),
              ...createTrackingProps({ elementName: 'Onboarding Steps: Set Up Notification Preferences' }),
            }}
          />
        </div>
      </div>
      {supportedEhr && (
        <EhrIntegrationModal
          open={modalOpen === 'ehrIntegration'}
          onClose={() => setModalOpen(null)}
          ehr={supportedEhr as SupportedEhrName}
        />
      )}
      <AddBatchPhoneNumbersModal
        open={modalOpen === 'addPhoneNumbers'}
        onClose={() => setModalOpen(null)}
        existingNumbers={phoneNumbersData?.data?.phone_numbers || []}
        isLoading={isLoadingPhoneNumbers}
      />

      <SendFirstPrescriptionModal open={modalOpen === 'sendFirstPrescription'} onClose={() => setModalOpen(null)} />
    </div>
  );
};
